import React from 'react';
import { X, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';

export const RateLimitExceededModal = ({ isOpen, onClose, rateLimit }) => {
  const [isSpanish] = useLanguage();

  if (!isOpen) return null;

  const remaining = rateLimit?.remaining ?? 0;
  const maxRequests = rateLimit?.maxRequests ?? 5;
  const resetTime = rateLimit?.resetTime;
  
  // Format the reset time for display
  const formatResetTime = (timestamp) => {
    if (!timestamp) return null;
    try {
      // Clean the timestamp string (remove any extra whitespace or characters)
      const cleanTimestamp = timestamp.trim().replace(/[^\dTZ.:-]/g, '');
      
      const date = new Date(cleanTimestamp);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // Try to show the raw timestamp as fallback
        return timestamp;
      }
      
      return date.toLocaleString(isSpanish ? 'es-ES' : 'en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (e) {
      return timestamp; // Return original if parsing fails
    }
  };
  
  const formattedResetTime = resetTime ? formatResetTime(resetTime) : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 animate-modalEnter max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-red-500">
              {isSpanish ? 'Límite de Solicitudes Excedido' : 'Rate Limit Exceeded'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="mt-6">
          <div className="mb-6 p-4 bg-red-50 rounded-lg ">
            <p className="text-sm text-gray-800 mb-3">
              {isSpanish 
                ? 'Has alcanzado el límite máximo de solicitudes de demostración.'
                : 'You have reached the maximum limit of demo requests.'}
            </p>
            {formattedResetTime && (
              <div className="mt-3 pt-3 border-t border-red-200">
                <p className="text-xs text-gray-600 mb-1">
                  {isSpanish 
                    ? 'Puedes intentar de nuevo después de:'
                    : 'You can try again after:'}
                </p>
                <p className="text-sm font-semibold text-gray-800">
                  {formattedResetTime}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm text-gray-800">
            <p className="font-semibold mb-2">
              {isSpanish 
                ? '¿Qué puedes hacer?'
                : 'What can you do?'}
            </p>
            <ul className="space-y-2 list-disc list-inside">
              <li>
                {isSpanish 
                  ? 'Espera hasta que se restablezca el límite de solicitudes'
                  : 'Wait until the request limit resets'}
              </li>
              <li>
                {isSpanish 
                  ? 'Contacta con Atlantis Digital para acceso ilimitado'
                  : 'Contact Atlantis Digital for unlimited access'}
              </li>
            </ul>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-800 mb-2">
              {isSpanish 
                ? 'Para acceso ilimitado y funciones completas,'
                : 'For unlimited access and full features,'}
            </p>
            <p className="text-sm text-gray-800">
              {isSpanish 
                ? 'por favor contacte con '
                : 'please contact '}
              <a 
                href="https://www.atlantisdigital.nl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-default font-bold hover:underline"
              >
                Atlantis Digital
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RateLimitExceededModal;

